import { Component, EventEmitter, OnInit, Output, ViewChild, ChangeDetectionStrategy, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { Table } from 'primeng/table';
import { Extrato } from '../../../models/extrato.model';
import { LinhaExtrato } from '../../../models/linhaExtrato.model';
import { ExtratoService } from '../../../services/importacao-extrato.service';
import { Router } from '@angular/router';
import { SpinnerService } from '../../../services/spinner.service';
import { ToastService } from '../../../services/toast.service';
import { SeveridadeToast } from '../../../models/enums/severidade-toast.enum';
import { Icones } from '../../../shared/variaveisDeRotas';
import { JwtHelper } from '../../../helpers/jwtDecode.helper';
import { ContaBancariaService } from '../../../services/conta-bancaria.service';
import { PlanoDeContasService } from '../../../services/plano-de-contas.service';
import { forkJoin, map, Subject, takeUntil } from 'rxjs';
import { retornaDescricao } from '../../../shared/mock/descricao';
import { ConfirmationService } from 'primeng/api';

interface LinhaExtratoEdicao extends LinhaExtrato {
  contaContabilIdOriginal: string;
  permiteAlterarContaContrapartida: boolean;
}

@Component({
  selector: 'app-lista-extrato',
  templateUrl: './lista-extrato.component.html',
  styleUrls: ['./lista-extrato.component.scss'],
  providers: [ConfirmationService],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ListaExtratoComponent implements OnInit, OnDestroy {
  @Output() tituloChange = new EventEmitter<string>();
  @Output() iconeChange = new EventEmitter<string>();

  @Output() tituloDescricao = new EventEmitter<string>();
  @Output() textoDescricao = new EventEmitter<string>();
  @Output() mostrarDescricao = new EventEmitter<boolean>();

  @ViewChild('dt') dt: Table | undefined;

  Extratos: Extrato[] = [];
  loading: boolean = false;
  userId: string;
  selectedExtrato: Extrato | null = null;
  detailsDialogVisible: boolean = false;

  editDialogVisible = false;
  editExtrato: Extrato | null = null;
  editLinhas: LinhaExtratoEdicao[] = [];
  contasContabeis: { nome: string; id: string }[] = [];
  salvandoEdicao = false;

  private cacheNomesBancos = new Map<string, string>();
  private destroy$ = new Subject<void>();

  constructor(
    private extratoService: ExtratoService,
    private router: Router,
    private spinnerService: SpinnerService,
    private toastService: ToastService,
    private jwtHelper: JwtHelper,
    private contaBancariaService: ContaBancariaService,
    private planoDeContasService: PlanoDeContasService,
    private confirmationService: ConfirmationService,
    private cdr: ChangeDetectorRef
  ) {
    this.userId = this.jwtHelper.getUserIdFromToken();
  }

  ngOnInit(): void {
    this.tituloChange.emit('Extratos');
    this.iconeChange.emit(Icones.listaTopicoAmarelo);
    this.tituloDescricao.emit(retornaDescricao('listarExtratoBancario').titulo);
    this.textoDescricao.emit(retornaDescricao('listarExtratoBancario').texto);
    this.mostrarDescricao.emit(true);
    this.carregarExtratos();
    this.carregarContasContabeis();
  }

  ngOnDestroy(): void {
    this.mostrarDescricao.emit(false);
    this.destroy$.next();
    this.destroy$.complete();
    this.cacheNomesBancos.clear();
  }

  private carregarContasContabeis(): void {
    this.planoDeContasService.getContasAnaliticasPorUsuario(this.userId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (contas) => {
          this.contasContabeis = contas.map(conta => ({
            nome: conta.descricao,
            id: String(conta.id).toLowerCase()
          }));
          this.cdr.markForCheck();
        },
        error: () => {
          this.toastService.setMensagemToast('Erro ao carregar contas contábeis.', SeveridadeToast.Error);
        }
      });
  }

  carregarExtratos() {
    this.loading = true;
    this.spinnerService.show('Carregando extratos...');

    this.extratoService.getExtratosComLinhasExtrato(this.userId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data: Extrato[]) => {
          const bancosUnicos = [...new Set(data.filter(e => e.ehPlanilha).map(e => e.nomeBanco))];

          if (bancosUnicos.length === 0) {
            const extratosComMesAno = data.map(extrato => ({
              ...extrato,
              mesAno: `${extrato.mes}/${extrato.ano}`
            }));

            this.Extratos = extratosComMesAno;
            this.extratoService.setExtratos(extratosComMesAno);
            this.loading = false;
            this.spinnerService.hide();
            this.cdr.markForCheck();
            return;
          }

          const bancosParaBuscar = bancosUnicos.filter(codigo => !this.cacheNomesBancos.has(codigo));

          if (bancosParaBuscar.length === 0) {
            const extratosAtualizados = data.map(extrato => {
              const extratoAtualizado = { ...extrato };

              if (extrato.ehPlanilha && this.cacheNomesBancos.has(extrato.nomeBanco)) {
                extratoAtualizado.nomeBanco = this.cacheNomesBancos.get(extrato.nomeBanco);
              }
              extratoAtualizado.mesAno = `${extrato.mes}/${extrato.ano}`;

              return extratoAtualizado;
            });

            this.Extratos = extratosAtualizados;
            this.extratoService.setExtratos(extratosAtualizados);
            this.loading = false;
            this.spinnerService.hide();
            this.cdr.markForCheck();
            return;
          }

          const bancoObservables = bancosParaBuscar.map(codigo =>
            this.contaBancariaService.obterBancoPorCodigo(codigo).pipe(
              map(banco => ({ codigo, nome: banco.name })),
              takeUntil(this.destroy$)
            )
          );

          forkJoin(bancoObservables).subscribe({
            next: (bancos: { codigo: string, nome: string }[]) => {
              bancos.forEach(b => this.cacheNomesBancos.set(b.codigo, b.nome));

              const extratosAtualizados = data.map(extrato => {
                const extratoAtualizado = { ...extrato };

                if (extrato.ehPlanilha && this.cacheNomesBancos.has(extrato.nomeBanco)) {
                  extratoAtualizado.nomeBanco = this.cacheNomesBancos.get(extrato.nomeBanco);
                }
                extratoAtualizado.mesAno = `${extrato.mes}/${extrato.ano}`;

                return extratoAtualizado;
              });

              this.Extratos = extratosAtualizados;
              this.extratoService.setExtratos(extratosAtualizados);
              this.loading = false;
              this.spinnerService.hide();
              this.cdr.markForCheck();
            },
            error: (error) => {
              console.error('Erro ao carregar nomes dos bancos:', error);
              const extratosComMesAno = data.map(extrato => ({
                ...extrato,
                mesAno: `${extrato.mes}/${extrato.ano}`
              }));

              this.Extratos = extratosComMesAno;
              this.extratoService.setExtratos(extratosComMesAno);
              this.loading = false;
              this.spinnerService.hide();
              this.cdr.markForCheck();
              this.toastService.setMensagemToast('Extratos carregados, mas alguns nomes de bancos não puderam ser obtidos.', SeveridadeToast.Warn);
            }
          });
        },
        error: (error) => {
          console.error('Erro ao carregar extratos:', error);
          this.loading = false;
          this.spinnerService.hide();
          this.cdr.markForCheck();
          this.toastService.setMensagemToast('Erro ao carregar extratos.', SeveridadeToast.Error);
        }
      });
  }

  applyFilterGlobal(value: string): void {
    if (this.dt) {
      this.dt.filterGlobal(value, 'contains');
    }
  }

  verDetalhesListaExtrato(extrato: Extrato) {
    this.selectedExtrato = {
      ...extrato,
      mesAno: `${extrato.mes}/${extrato.ano}`,
      linhasExtratos: extrato.linhasExtratos ? [...extrato.linhasExtratos] : []
    };
    this.detailsDialogVisible = true;

    setTimeout(() => {
      this.cdr.detectChanges();
    }, 0);
  }

  abrirEdicaoExtrato(extrato: Extrato): void {
    this.editExtrato = {
      ...extrato,
      mesAno: `${extrato.mes}/${extrato.ano}`,
      linhasExtratos: extrato.linhasExtratos ? [...extrato.linhasExtratos] : []
    };

    this.editLinhas = (extrato.linhasExtratos || []).map(linha => {
      const contaId = String(linha.contaContabilId || '').toLowerCase();
      return {
        ...linha,
        contaContabilId: contaId,
        contaContabilIdOriginal: contaId,
        permiteAlterarContaContrapartida: false,
        descricaoContaContabil: linha.descricaoContaContabil
          || (linha as any).contaContabilDescricao
          || this.contasContabeis.find(c => c.id === contaId)?.nome
          || ''
      };
    });

    this.editDialogVisible = true;
    this.cdr.markForCheck();
  }

  onToggleAlterarConta(linha: LinhaExtratoEdicao): void {
    if (!linha.permiteAlterarContaContrapartida) {
      linha.contaContabilId = linha.contaContabilIdOriginal;
    }
    this.cdr.markForCheck();
  }

  get quantidadeAlteracoesPendentes(): number {
    return this.editLinhas.filter(l =>
      l.permiteAlterarContaContrapartida
      && l.contaContabilId
      && l.contaContabilId !== l.contaContabilIdOriginal
    ).length;
  }

  fecharEdicaoExtrato(): void {
    this.editDialogVisible = false;
    this.editExtrato = null;
    this.editLinhas = [];
    this.salvandoEdicao = false;
    this.cdr.markForCheck();
  }

  salvarEdicaoExtrato(): void {
    const linhasAlteradas = this.editLinhas.filter(l =>
      l.permiteAlterarContaContrapartida
      && l.contaContabilId
      && l.contaContabilId !== l.contaContabilIdOriginal
    );

    if (linhasAlteradas.length === 0) {
      this.toastService.setMensagemToast(
        'Marque o checkbox e selecione uma conta diferente para ao menos uma linha.',
        SeveridadeToast.Warn
      );
      return;
    }

    const payload = {
      usuarioId: this.userId,
      linhas: linhasAlteradas.map(l => ({
        linhaExtratoId: l.id,
        novaContaContabilId: l.contaContabilId,
        permiteAlterarContaContrapartida: true
      }))
    };

    // Fecha o modal antes do spinner para não ficar atrás do overlay do p-dialog.
    this.fecharEdicaoExtrato();
    this.salvandoEdicao = true;
    this.spinnerService.show('Atualizando contas contábeis e saldos...');
    this.cdr.detectChanges();

    this.extratoService.atualizarContasContabeisLinhasExtrato(payload)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.toastService.setMensagemToast(
            `${payload.linhas.length} linha(s) atualizada(s) com sucesso.`,
            SeveridadeToast.Success
          );
          this.Extratos = [];
          this.extratoService.setExtratos([]);
          this.cdr.markForCheck();
          this.spinnerService.show('Atualizando listagem de extratos...');
          this.carregarExtratos();
        },
        error: (e) => {
          const mensagem = typeof e.error === 'string'
            ? e.error
            : e.error?.message || 'Erro ao atualizar contas contábeis do extrato.';
          this.toastService.setMensagemToast(mensagem, SeveridadeToast.Error);
          this.salvandoEdicao = false;
          this.spinnerService.hide();
          this.cdr.markForCheck();
          this.carregarExtratos();
        }
      });
  }

  importarExtrato() {
    this.spinnerService.show('Redirecionando para importação...');
    setTimeout(() => {
      this.router.navigate(['/importacao-extrato-bancario']);
      this.spinnerService.hide();
    }, 500);
  }

  exportarExtrato(extrato: Extrato) {
    this.spinnerService.show('Exportando extrato...');
    setTimeout(() => {
      this.spinnerService.hide();
      this.toastService.setMensagemToast('Extrato exportado com sucesso!', SeveridadeToast.Success);
    }, 2000);
  }

  excluirExtrato(extrato: Extrato) {
    const periodo = extrato.mesAno || `${extrato.mes}/${extrato.ano}`;
    this.confirmationService.confirm({
      message: `Tem certeza que deseja excluir o extrato de ${extrato.nomeBanco} (${periodo})? Os lançamentos contábeis vinculados serão revertidos e esta ação não poderá ser desfeita.`,
      header: 'Confirmar Exclusão',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Sim, excluir',
      rejectLabel: 'Cancelar',
      acceptButtonStyleClass: 'p-button-danger',
      accept: () => {
        this.spinnerService.show('Excluindo extrato...');
        this.extratoService.excluirExtrato(extrato.id, this.userId).subscribe({
          next: () => {
            if (this.selectedExtrato?.id === extrato.id) {
              this.detailsDialogVisible = false;
              this.selectedExtrato = null;
            }
            if (this.editExtrato?.id === extrato.id) {
              this.fecharEdicaoExtrato();
            }
            this.toastService.setMensagemToast('Extrato excluído com sucesso.', SeveridadeToast.Success);
            this.carregarExtratos();
          },
          error: (e) => {
            const mensagem = typeof e.error === 'string'
              ? e.error
              : e.error?.message || 'Erro ao excluir extrato.';
            this.toastService.setMensagemToast(mensagem, SeveridadeToast.Error);
            this.spinnerService.hide();
          }
        }).add(() => {
          this.spinnerService.hide();
        });
      }
    });
  }

  obterDescricaoContaLinha(linha: LinhaExtrato): string {
    return linha.descricaoContaContabil
      || (linha as any).contaContabilDescricao
      || this.contasContabeis.find(c => c.id === linha.contaContabilId)?.nome
      || '-';
  }

  formatarData(data: string | Date): string {
    if (!data) return '-';

    try {
      if (data instanceof Date) {
        return data.toLocaleDateString('pt-BR');
      }
      if (typeof data === 'string' && data.includes('T')) {
        const dataObj = new Date(data);
        if (!isNaN(dataObj.getTime())) {
          return dataObj.toLocaleDateString('pt-BR');
        }
      }

      if (typeof data === 'string' && data.includes('-') && !data.includes('T')) {
        const partes = data.split('-');

        if (partes.length === 3) {
          const parte1 = parseInt(partes[0]);
          const parte2 = parseInt(partes[1]);
          const ano = parseInt(partes[2]);

          let dia, mes;

          if (parte2 === 12) {
            dia = parte1;
            mes = parte2;
          } else if (parte1 === 12) {
            mes = parte1;
            dia = parte2;
          } else if (parte1 <= 12 && parte2 <= 12) {
            dia = parte1;
            mes = parte2;
          } else if (parte1 <= 12 && parte2 > 12) {
            dia = parte2;
            mes = parte1;
          } else {
            dia = parte1;
            mes = parte2;
          }

          if (dia >= 1 && dia <= 31 && mes >= 1 && mes <= 12 && ano >= 1900) {
            const diaFormatado = dia.toString().padStart(2, '0');
            const mesFormatado = mes.toString().padStart(2, '0');
            return `${diaFormatado}/${mesFormatado}/${ano}`;
          }
        }
      }

      const dataObj = new Date(data);

      if (isNaN(dataObj.getTime())) {
        return String(data);
      }

      return dataObj.toLocaleDateString('pt-BR');
    } catch (error) {
      console.warn('Erro ao formatar data:', data, error);
      return String(data || '-');
    }
  }
}
